import { api, readsApi, writesApi } from './api';
import type { Product, Category } from '@apptypes/index';

export interface AdminProductRow {
  id: string;
  name: string;
  slug?: string | null;
  sku?: string | null;
  /** NUMERIC(15,2) KES (not cents). */
  basePrice: number;
  isActive: boolean;
  status: string;
  primaryImage?: string | null;
  stock?: number | null;
  businessId: string;
  businessName: string;
  businessStatus?: string | null;
  branchId: string;
  branchName?: string | null;
  categoryName?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface AdminProductListParams {
  q?: string;
  businessId?: string;
  branchId?: string;
  categoryId?: string;
  status?: 'active' | 'inactive' | 'all';
  sort?: 'createdAt' | 'name' | 'price';
  order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface AdminProductListResponse {
  data: AdminProductRow[];
  pagination: { page: number; limit: number; total: number };
}

export const productsService = {
  // Platform-wide product oversight → FastAPI :8000 (paginated, filtered read)
  adminList: (params?: AdminProductListParams) =>
    readsApi.get<AdminProductListResponse>('/admin/products', params as Record<string, unknown>),

  // Takedown / restore → Spring Boot :8080 (writes)
  adminUnpublish: (productId: string, reason?: string) =>
    writesApi.patch(`/admin/products/${productId}/unpublish`, reason ? { reason } : {}),

  adminPublish: (productId: string) =>
    writesApi.patch(`/admin/products/${productId}/publish`, {}),

  // Read (FastAPI)
  get: (productId: string) =>
    api.get<Product>(`/products/${productId}`),

  // Write (Spring Boot): scoped to business/branch
  create: (businessId: string, branchId: string, data: Partial<Product>) =>
    api.post<Product>(`/businesses/${businessId}/branches/${branchId}/products`, data),

  update: (businessId: string, branchId: string, productId: string, data: Partial<Product>) =>
    api.put<Product>(`/businesses/${businessId}/branches/${branchId}/products/${productId}`, data),

  activate: (businessId: string, branchId: string, productId: string) =>
    api.patch(`/businesses/${businessId}/branches/${branchId}/products/${productId}/activate`),

  deactivate: (businessId: string, branchId: string, productId: string) =>
    api.patch(`/businesses/${businessId}/branches/${branchId}/products/${productId}/deactivate`),

  delete: (businessId: string, branchId: string, productId: string) =>
    api.delete(`/businesses/${businessId}/branches/${branchId}/products/${productId}`),

  // Categories (FastAPI reads)
  listCategories: () =>
    api.get<Category[]>('/categories'),
};
